// Pseudo-table context inference for the enclosing statement.
//
// Given the cursor offset, decide which pseudo-table context (MERGE branch,
// OUTPUT, ON CONFLICT, ON DUPLICATE KEY UPDATE, trigger body, or none) the
// cursor sits in, so collectPseudoTablesForContext can inject the right
// engine-specific pseudo records (`inserted`/`deleted`, `excluded`, `NEW`/`OLD`,
// …) into the Scope.
//
// §4.5 (Plan 19 Task 3): the MERGE / OUTPUT / ON CONFLICT / ON DUPLICATE scan is
// STRUCTURAL — a `scanTopLevelKeywords` pass over `[stmt.start, offset)` that is
// string/comment/paren-safe and unbounded within the statement. It replaces the
// old 400-char backward `slice().toUpperCase().includes()` window, which had two
// defect classes: (1) a WHEN body longer than 400 chars lost `inserted`/`deleted`
// beyond the cap; (2) the phrases matched inside string literals and comments
// (`'WHEN MATCHED'`, `-- ON CONFLICT`). Merge-branch kinds additionally require
// the enclosing statement to be a MERGE (stmt.kind, with a head-keyword
// fallback) so a `CASE WHEN matched` in a non-MERGE statement can never be
// mistaken for a merge branch.
//
// Deliberately NOT routed through classifyPosition: the classifier carries clause
// state, not MERGE-branch state, and importing it here would reintroduce the
// circular dep the original window-scan was chosen to avoid. `scanTopLevelKeywords`
// lives in `structure/` and carries no cycle.
//
// PRD: Ralph/SemanticResolverReadOnly/PRD.md § Slice: Scope Resolver —
// subqueries + pseudo-tables + resolveScope assembly.

import type { ParsedBuffer, StatementInfo } from '../types';
import { scanTopLevelKeywords, type TopKeyword } from '../structure/scanner';
import type { PseudoTableContext } from './pseudoTables';

export function inferPseudoContext(
  buffer: ParsedBuffer,
  offset: number,
  stmt: StatementInfo | null,
): PseudoTableContext {
  // Authoritative trigger-body signal: the coarse classifier already labelled
  // the enclosing statement as a procedural body (e.g. MSSQL anonymous
  // BEGIN…END / trigger batch). No engine gate is needed — engine-specificity
  // of the emitted pseudo-tables is handled downstream in
  // collectPseudoTablesForContext. Kept byte-identical to the pre-split logic.
  if (stmt && stmt.kind === 'procedural-body') return 'trigger-body';

  const { source, options } = buffer.parsedDocument;
  const start = stmt ? stmt.range.startOffset : Math.max(0, offset);

  // Structural scan of the statement prefix. Records EVERY unquoted depth-0
  // identifier (table/column names included), so keyword-group detection matches
  // multi-word groups from consecutive keyword hits.
  const keywords = scanTopLevelKeywords(source, start, offset, options);
  // Merge-branch gate: coarseKind classifies MERGE statements — including the
  // WITH-led `WITH x AS (…) MERGE …` T-SQL shape — as kind 'merge', so
  // stmt.kind is the authoritative signal; the head-keyword check covers
  // callers that pass a null / unkinded statement.
  const isMerge = stmt?.kind === 'merge'
    || (keywords.length > 0 && keywords[0].upper === 'MERGE');
  const structural = classifyPseudoContextFromKeywords(keywords, isMerge);
  if (structural !== 'none') return structural;

  // Oracle `CREATE [OR REPLACE] TRIGGER … FOR EACH ROW BEGIN …` coarse-
  // classifies as 'ddl-create' (not 'procedural-body'), so the stmt.kind cue
  // above never fires for it. Inside the body the backward window still holds
  // the `TRIGGER` keyword followed by the body-opening `BEGIN`; require that
  // ordering so a `CREATE TRIGGER` header position (before BEGIN) stays 'none'.
  // This arm stays as the classifier-gap workaround — byte-identical to the
  // pre-split heuristic (decision 5, brief step (e)).
  if (options.engine === 'oracle') {
    const window = source.slice(Math.max(0, offset - 400), offset).toUpperCase();
    const trgIdx = window.lastIndexOf('TRIGGER');
    if (trgIdx >= 0 && window.indexOf('BEGIN', trgIdx) >= 0) return 'trigger-body';
  }
  return 'none';
}

// Walk the depth-0 keyword stream and return the pseudo-table context of the
// LAST recognized keyword group before the cursor (the scan range already ends
// at the cursor). Merge-branch groups (`WHEN [NOT] MATCHED [BY SOURCE]`) require
// `isMerge` (the enclosing statement is a MERGE); OUTPUT / ON CONFLICT /
// ON DUPLICATE KEY UPDATE are self-identifying multi-word groups. The per-group
// classification mirrors the retired window scan's priority order (BY SOURCE
// before the bare NOT-MATCHED target form).
function classifyPseudoContextFromKeywords(
  keywords: readonly TopKeyword[],
  isMerge: boolean,
): PseudoTableContext {
  let context: PseudoTableContext = 'none';
  for (let k = 0; k < keywords.length; k++) {
    const group = matchGroupAt(keywords, k, isMerge);
    if (group !== null) context = group; // last group before the cursor wins
  }
  return context;
}

// eslint-disable-next-line complexity
function matchGroupAt(
  keywords: readonly TopKeyword[],
  k: number,
  isMerge: boolean,
): PseudoTableContext | null {
  const at = (n: number): string | undefined => keywords[k + n]?.upper;
  const kw = keywords[k].upper;

  if (isMerge && kw === 'WHEN') {
    if (at(1) === 'MATCHED') return 'merge-when-matched';
    if (at(1) === 'NOT' && at(2) === 'MATCHED') {
      if (at(3) === 'BY' && at(4) === 'SOURCE') return 'merge-when-not-matched-source';
      return 'merge-when-not-matched-target';
    }
    return null;
  }
  if (kw === 'OUTPUT') return 'output';
  if (kw === 'ON') {
    if (at(1) === 'CONFLICT') return 'on-conflict-do-update';
    if (at(1) === 'DUPLICATE' && at(2) === 'KEY' && at(3) === 'UPDATE') {
      return 'insert-duplicate-key-update';
    }
  }
  return null;
}
